/**
 * Tiny cookie helper used by the cross-domain auth bridge.
 * Mirror of sandbox/src/Core/utils/cookieUtils.js — kept in sync intentionally.
 */

import { decrypt } from "./other";

const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

export const setCookie = (name, value, opts = {}) => {
  const {
    domain,
    path = "/",
    maxAge,
    sameSite = "Lax",
    secure = true,
  } = opts;

  const isHttpLocalhost =
    window.location.protocol === "http:" &&
    window.location.hostname === "localhost";

  const parts = [`${name}=${encodeURIComponent(value)}`, `Path=${path}`];
  if (domain) parts.push(`Domain=${domain}`);
  if (typeof maxAge === "number") parts.push(`Max-Age=${maxAge}`);
  parts.push(`SameSite=${sameSite}`);
  if (secure && !isHttpLocalhost) parts.push("Secure");

  document.cookie = parts.join("; ");
};

export const getCookie = (name) => {
  const target = `${name}=`;
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  for (const c of cookies) {
    if (c.startsWith(target)) {
      try {
        return decodeURIComponent(c.slice(target.length));
      } catch {
        return null;
      }
    }
  }
  return null;
};

export const deleteCookie = (name, opts = {}) => {
  const { domain, path = "/" } = opts;
  const parts = [`${name}=`, `Path=${path}`, "Max-Age=0"];
  if (domain) parts.push(`Domain=${domain}`);
  document.cookie = parts.join("; ");
};

/**
 * Returns the candidate cookie Domain attributes a cookie on `hostname` could
 * have been set with: every parent suffix of at least two labels, dot-prefixed.
 *
 *   "studentportal.dev.codeyoung.com" → [".dev.codeyoung.com", ".codeyoung.com"]
 *   "localhost"                       → []   (host-only cookies have no Domain)
 */
const candidateCookieDomains = (hostname) => {
  if (!hostname || hostname === "localhost") return [];
  const labels = hostname.split(".");
  const domains = [];
  for (let i = 0; i <= labels.length - 2; i++) {
    domains.push("." + labels.slice(i).join("."));
  }
  return domains;
};

/**
 * THE canonical shared cookie Domain — the single source of truth for every
 * cross-app cookie write (auth tokens, `cy_auth_sync`, `cy_logged_out`). Derived
 * purely from the current host (drop the leftmost label), so it is deterministic,
 * ENV-INDEPENDENT, env-ISOLATED, and resolves to the SAME value in both apps:
 *
 *   studentportal.codeyoung.com / sandbox.codeyoung.com         → .codeyoung.com
 *   studentportal.dev.codeyoung.com / sandbox.dev.codeyoung.com → .dev.codeyoung.com
 *   localhost                                                   → undefined (host-only)
 *
 * MUST resolve the same value as Sandbox's cookieUtils.getSharedCookieDomain.
 */
export const getSharedCookieDomain = () => {
  const host = window.location.hostname;
  if (host === "localhost") return undefined;
  const labels = host.split(".");
  if (labels.length >= 3) return "." + labels.slice(1).join(".");
  if (labels.length === 2) return "." + host;
  return undefined;
};

/**
 * Deletes a cookie across every domain it might have been set with — host-only
 * plus each parent suffix of the current hostname. Use this whenever the exact
 * Domain a cookie was written with isn't known at the delete site: a cookie set
 * with `Domain=.codeyoung.com` can only be removed by re-specifying that same
 * Domain, so a plain host-only `deleteCookie(name)` silently no-ops on it.
 */
export const deleteCookieAllDomains = (name) => {
  const domains = [undefined, ...candidateCookieDomains(window.location.hostname)];
  domains.forEach((domain) => deleteCookie(name, { domain }));
};

/**
 * Deletes the given auth cookies across every domain they might have been set
 * with — host-only plus each parent suffix of the current hostname. Needed
 * because the same cookie may have been written host-only (localhost), on the
 * shared cross-app domain (`.dev.codeyoung.com`), and/or on `.codeyoung.com`.
 * Clearing only one variant would leave a stale cookie that re-bootstraps a
 * session after logout.
 *
 * NOTE: unlike Sandbox's clearAuthCookies, this does NOT notify the backend —
 * StudentPortal's `logoutUser` (utils/auth.js) owns the cy-sso `api/logout`
 * call, so adding it here would fire logout twice.
 *
 * @param {string[]} [names=["Authorization", "refresh-token", "cy_auth_sync"]]
 */
export const clearAuthCookies = (
  names = ["Authorization", "refresh-token", "cy_auth_sync"]
) => {
  names.forEach((name) => deleteCookieAllDomains(name));
  // Raise the cross-app logout signal so an already-open Sandbox tab logs out
  // deterministically. Must run while the session is still readable in
  // localStorage (logoutUser clears storage AFTER calling this — see auth.js).
  setLoggedOutFlag();
};

/**
 * Shared cross-app logout signal — mirror of Sandbox's `cy_logged_out` handling.
 *
 * The deterministic "the session ended" handshake between the two standalone
 * tabs. Inferring a cross-app logout from API 401 patterns is unreliable (the
 * refresh call can still 200, and unrelated 2xx responses reset any failure
 * counter), so logout/expiry raises this flag on the shared parent domain and
 * every other entry point trusts it.
 *
 * Student-only guard: only a STUDENT session participates in cross-app
 * auto-login / auto-logout (a role we can't read defaults to raising the signal,
 * so a genuine student logout is never silently dropped).
 */
export const LOGGED_OUT_FLAG = "cy_logged_out";

export const setLoggedOutFlag = () => {
  let role;
  try {
    role = decrypt(localStorage.getItem("user"))?.role;
  } catch (_) {
    role = undefined;
  }
  if (role && role !== "student") return; // parents / staff: app-local only

  // Set on the SINGLE canonical shared domain (env-scoped: `.dev.codeyoung.com`
  // on dev, `.codeyoung.com` on prod, host-only on localhost). NOT on broader
  // parents — writing `.codeyoung.com` from a dev host would leak the logout to
  // prod tabs (and vice versa). The sibling app in the same env still sees it.
  setCookie(LOGGED_OUT_FLAG, String(Date.now()), {
    domain: getSharedCookieDomain(),
    maxAge: ONE_YEAR_SECONDS,
    sameSite: "Lax",
  });
};

/** True when a logout signal raised by either app is present. */
export const isLoggedOut = () => !!getCookie(LOGGED_OUT_FLAG);

/** Clears the logout signal — called on a fresh login so the session revives. */
export const clearLoggedOutFlag = () => deleteCookieAllDomains(LOGGED_OUT_FLAG);

/**
 * Returns the most-specific parent domain shared by two hostnames, prefixed
 * with a dot. Returns "localhost" for both-localhost (no dot — port-agnostic).
 * Returns null if the only shared suffix is a single label (e.g. "com").
 */
export const computeSharedCookieDomain = (hostA, hostB) => {
  if (!hostA || !hostB) return null;
  if (hostA === "localhost" && hostB === "localhost") return "localhost";

  const partsA = hostA.split(".");
  const partsB = hostB.split(".");

  const shared = [];
  let i = partsA.length - 1;
  let j = partsB.length - 1;
  while (i >= 0 && j >= 0 && partsA[i] === partsB[j]) {
    shared.unshift(partsA[i]);
    i--;
    j--;
  }

  if (shared.length < 2) return null;
  return `.${shared.join(".")}`;
};
